import { useState, useEffect } from 'react';
import { CreateForumModal } from './CreateForumModal';
import { CreateTribeModal } from './CreateTribeModal';
import { UniversityTribeCreationModal } from './UniversityTribeCreationModal';
import { PassOwnershipModal } from './PassOwnershipModal';
import { useUserData } from './UserDataContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Users, GraduationCap } from 'lucide-react';

export function GovernanceModalManager() {
    const { userProfile } = useUserData();
    const [showCreateForum, setShowCreateForum] = useState(false);
    const [showCreateTribe, setShowCreateTribe] = useState(false);
    const [showTribeTypeSelector, setShowTribeTypeSelector] = useState(false);
    const [showUniversityTribe, setShowUniversityTribe] = useState(false);

    // Pass Ownership State
    const [passOwnershipState, setPassOwnershipState] = useState<{
        open: boolean;
        entityType: 'forum' | 'tribe';
        entityId: string;
        lastTransferAt?: any;
    }>({
        open: false,
        entityType: 'tribe',
        entityId: '',
    });

    useEffect(() => {
        const handleOpenCreateForum = () => setShowCreateForum(true);
        const handleOpenCreateTribe = () => {
            // Legion users see a pre-step to choose standard vs university tribe.
            // All other ranks go straight to the standard modal — behaviour unchanged.
            if (userProfile?.rank === 'legion') {
                setShowTribeTypeSelector(true);
            } else {
                setShowCreateTribe(true);
            }
        };

        const handleOpenPassOwnership = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (detail && detail.entityType && detail.entityId) {
                setPassOwnershipState({
                    open: true,
                    entityType: detail.entityType,
                    entityId: detail.entityId,
                    lastTransferAt: detail.lastTransferAt
                });
            }
        };

        window.addEventListener('openCreateForumModal', handleOpenCreateForum);
        window.addEventListener('openCreateTribeModal', handleOpenCreateTribe);
        window.addEventListener('openPassOwnershipModal', handleOpenPassOwnership);

        return () => {
            window.removeEventListener('openCreateForumModal', handleOpenCreateForum);
            window.removeEventListener('openCreateTribeModal', handleOpenCreateTribe);
            window.removeEventListener('openPassOwnershipModal', handleOpenPassOwnership);
        };
    }, [userProfile?.rank]);

    return (
        <>
            <CreateForumModal
                open={showCreateForum}
                onOpenChange={setShowCreateForum}
            />
            <CreateTribeModal
                open={showCreateTribe}
                onOpenChange={setShowCreateTribe}
            />
            <UniversityTribeCreationModal
                open={showUniversityTribe}
                onOpenChange={setShowUniversityTribe}
            />

            {/* Legion-only pre-step: choose tribe type before opening the creation form */}
            <Dialog open={showTribeTypeSelector} onOpenChange={setShowTribeTypeSelector}>
                <DialogContent className="max-w-sm bg-[#FDFBF7] dark:bg-[#1a1a1a] border-black/5 dark:border-white/10 p-6 shadow-2xl">
                    <DialogHeader className="mb-4">
                        <DialogTitle className="text-lg font-bold">
                            What kind of tribe?
                        </DialogTitle>
                        <DialogDescription className="text-sm text-black/60 dark:text-white/60">
                            Choose the type of tribe you want to establish.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid grid-cols-1 gap-3">
                        {/* Standard Tribe */}
                        <button
                            onClick={() => {
                                setShowTribeTypeSelector(false);
                                setShowCreateTribe(true);
                            }}
                            className="flex items-start gap-4 p-4 rounded-2xl border-2 border-black/8 dark:border-white/10 bg-black/2 dark:bg-white/3 hover:border-[#F39C12]/60 dark:hover:border-[#4478ea]/60 hover:bg-[#F39C12]/5 dark:hover:bg-[#4478ea]/5 transition-all text-left group"
                        >
                            <div className="p-2.5 rounded-xl bg-[#F39C12]/10 dark:bg-[#4478ea]/10 shrink-0 group-hover:bg-[#F39C12]/20 dark:group-hover:bg-[#4478ea]/20 transition-colors">
                                <Users className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                            </div>
                            <div>
                                <p className="font-bold text-sm text-black dark:text-white mb-0.5">Standard Tribe</p>
                                <p className="text-xs text-black/50 dark:text-white/50 leading-relaxed">A community for any topic or interest</p>
                            </div>
                        </button>

                        {/* University Tribe */}
                        <button
                            onClick={() => {
                                setShowTribeTypeSelector(false);
                                setShowUniversityTribe(true);
                            }}
                            className="flex items-start gap-4 p-4 rounded-2xl border-2 border-black/8 dark:border-white/10 bg-black/2 dark:bg-white/3 hover:border-[#FFD700]/60 dark:hover:border-[#FFD700]/60 hover:bg-[#FFD700]/5 dark:hover:bg-[#FFD700]/5 transition-all text-left group"
                        >
                            <div className="p-2.5 rounded-xl bg-[#FFD700]/10 shrink-0 group-hover:bg-[#FFD700]/20 transition-colors">
                                <GraduationCap className="w-5 h-5 text-[#b8860b] dark:text-[#FFD700]" />
                            </div>
                            <div>
                                <p className="font-bold text-sm text-black dark:text-white mb-0.5 flex items-center gap-1.5">
                                    University Tribe
                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#FFD700]/20 text-[#b8860b] dark:text-[#FFD700] uppercase tracking-wider">Legion</span>
                                </p>
                                <p className="text-xs text-black/50 dark:text-white/50 leading-relaxed">An official campus community. Ownership transfers to the founding member.</p>
                            </div>
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {passOwnershipState.open && (
                <PassOwnershipModal
                    open={passOwnershipState.open}
                    onOpenChange={(open) => setPassOwnershipState(prev => ({ ...prev, open }))}
                    entityType={passOwnershipState.entityType}
                    entityId={passOwnershipState.entityId}
                    lastTransferAt={passOwnershipState.lastTransferAt}
                />
            )}
        </>
    );
}
